import * as React from 'react';
import { hydrate } from 'react-dom';
import i18n from 'i18next';
import * as Sentry from '@sentry/browser';
import { setupMoment } from 'shared/utils/setupMoment';
import { createBrowserHistory } from 'history';
import { Reducer } from 'redux';
import { initServices } from './services';
import App from './App';
import { configureStore } from './configureStore';
import { cookieManager } from 'libs/cookie';
import { i18nextInterpolation } from './i18next';
import { getCookieDomain } from './domain';

const setupIntl = (): Promise<void> =>
  new Promise((resolve) => {
    if (global.Intl) {
      return resolve();
    }
    require.ensure(
      [
        'intl',
        'intl/locale-data/jsonp/en.js',
        'intl/locale-data/jsonp/ru.js',
        'intl/locale-data/jsonp/es.js',
      ],
      (require) => {
        require('intl');
        require('intl/locale-data/jsonp/en.js');
        require('intl/locale-data/jsonp/ru.js');
        require('intl/locale-data/jsonp/es.js');
        resolve();
      }
    );
  });

type StartAppClientArg = {
  RootComponent: React.ComponentType;
  componentsReducer: Reducer;
  rootSaga: any;
};

export async function startAppClient({
  RootComponent,
  componentsReducer,
  rootSaga,
}: StartAppClientArg) {
  const {
    config,
    reduxState,
    language,
    apolloState,
    timeStamp,
    i18n: i18nResource,
  } = window.__INITIAL_STATE__;

  cookieManager.fill(document.cookie, getCookieDomain(location.host));

  const history = createBrowserHistory();

  setupMoment(language);
  await setupIntl();

  const defaultNS = cookieManager.get('i18nextNs') || 'investments';

  await i18n.init({
    ...config.i18next,
    interpolation: {
      ...config.i18next.interpolation,
      ...i18nextInterpolation,
    },
    defaultNS,
    resources: i18nResource,
    lng: language,
    saveMissing: true,
    missingKeyHandler: (lngs: string[], ns: string, key: string) => {
      if (defaultNS === 'cimode') {
        return;
      }
      console.warn(`[i18next] missing key "${key}"`);
      Sentry.captureMessage(`[i18next] missing key "${key}"`, Sentry.Severity.Warning);
    },
  });

  const { accessToken, refreshToken } = reduxState.app;
  const services = initServices(config, {
    apollo: apolloState,
    i18nInstance: i18n,
    keycloak:
      accessToken && refreshToken
        ? { accessToken, refreshToken, serverTimestamp: timeStamp }
        : undefined,
    host: location.host,
  });

  const { store, runSaga } = configureStore<typeof reduxState>({
    componentsReducer,
    initialState: reduxState,
    history,
    services,
  });

  services.keycloakService.startAutoRefresher();

  runSaga(rootSaga);

  const root = (
    <App
      store={store}
      keycloak={services.keycloakService}
      apolloClient={services.apolloClient}
      history={history}
      i18n={i18n}
    >
      <RootComponent />
    </App>
  );

  hydrate(root, document.getElementById('app'));
}
